import { useState } from 'react';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/ui/Button';
import { Field, Input } from '@/ui/Field';
import { errorMessage } from '@/lib/api/client';
import { loadSession, useAuth } from '@/lib/auth';

export const Route = createFileRoute('/login')({
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  beforeLoad: async ({ context, search }) => {
    // Already signed in? Skip the form entirely.
    const user = await loadSession(context.queryClient);
    if (user) throw redirect({ to: search.redirect ?? '/' });
  },
  component: LoginScreen,
});

function LoginScreen() {
  const { redirect: target } = Route.useSearch();
  const navigate = useNavigate();
  const { signIn } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    signIn.mutate(
      { username, password },
      { onSuccess: () => void navigate({ to: target ?? '/' }) }
    );
  };

  return (
    <div className="dot-grid bg-canvas grid h-full place-items-center p-6">
      <div className="w-full max-w-[22rem]">
        {/* Wordmark. The console announces itself once, here, and never again. */}
        <div className="mb-7 flex items-center gap-2.5">
          <div className="bg-signal text-ink-inverse flex size-8 items-center justify-center rounded font-mono text-sm font-bold">
            KK
          </div>
          <div className="leading-tight">
            <div className="text-ink text-base font-semibold tracking-tight">
              KK POS Console
            </div>
            <div className="text-2xs text-ink-faint font-mono tracking-widest uppercase">
              Store operations
            </div>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="border-line bg-surface flex flex-col gap-3 rounded border p-4"
        >
          <Field label="Username" htmlFor="username">
            <Input
              id="username"
              name="username"
              autoFocus
              autoComplete="username"
              size="lg"
              value={username}
              onChange={(e) => setUsername(e.currentTarget.value)}
              required
            />
          </Field>

          <Field label="Password" htmlFor="password">
            <Input
              id="password"
              name="password"
              type="password"
              autoComplete="current-password"
              size="lg"
              value={password}
              onChange={(e) => setPassword(e.currentTarget.value)}
              required
            />
          </Field>

          {signIn.isError ? (
            <div className="border-negative/40 bg-negative-muted text-ink flex items-start gap-2 rounded border p-2 text-xs">
              <AlertCircle className="text-negative mt-px size-3.5 shrink-0" />
              <span>{errorMessage(signIn.error)}</span>
            </div>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={signIn.isPending}
            className="mt-1 justify-center"
            disabled={!username || !password}
          >
            Sign in
          </Button>
        </form>

        <p className="text-2xs text-ink-faint mt-3 text-center font-mono">
          Five failed attempts slow this form down. Ask a manager for a reset.
        </p>
      </div>
    </div>
  );
}
